import { Select } from "./ui/select.tsx";
import { notify } from "./feedback.ts";
import { ChannelName } from "./ui/channel.tsx";
import { useMutation } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api } from "./api.ts";
import { POLICY_LABELS as L } from "./labels.ts";
import { Button, ErrorText, Field } from "./ui.tsx";

// Channel rules shared by Playbooks › Channel rules (channel-rules.tsx): the
// kinds in the order a channel lists them, what applies a rule and where it
// comes from, and the form for a workspace rule.

export const POLICY_GROUPS: C.ActionPolicy["kind"][] = ["platform_rule", "zooi_default", "workspace_policy", "tactic_hypothesis"];
const DISCUSSION_CHANNELS = ["hackernews", "reddit", "stackexchange", "x", "web"];

function sourceUrl(source: string | null | undefined) {
  if (!source) return null;
  try { const url = new URL(source); return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password ? url.href : null; } catch { return null; }
}

export function PolicyMetadata({ source, verification, checked_at, enforcement }: {
  source?: string | null; verification?: "verified" | "pending" | null; checked_at?: string | null; enforcement?: C.ActionPolicy["enforcement"];
}) {
  const url = sourceUrl(source);
  return <div className="z-flow">
    {enforcement && <p className="z-small z-muted">{L.enforcement[enforcement]}</p>}
    {(verification || url) && <p className="z-inline z-small z-muted">
      {verification && <span>{verification === "verified" && checked_at ? L.sourceChecked(checked_at) : L.sourcePending}</span>}
      {url && <a className="z-link" href={url} target="_blank" rel="noreferrer noopener">{L.source}</a>}
    </p>}
  </div>;
}

export function WorkspacePolicyForm({ initial, onSaved }: { initial?: C.WorkspacePolicy; onSaved: () => Promise<unknown> }) {
  const [form, setForm] = useState<C.CreateWorkspacePolicyInput>(() => initial ? { rule: initial.rule, channels: initial.channels, enforcement: initial.enforcement } : { rule: "", channels: [], enforcement: "checklist" });
  const options = [...new Set([...DISCUSSION_CHANNELS, ...(initial?.channels ?? [])])];
  const save = useMutation({
    mutationFn: () => initial ? api.patch<C.WorkspacePolicy>("/api/workspace/policies/" + initial.id, form) : api.post<C.WorkspacePolicy>("/api/workspace/policies", form),
    onSuccess: async () => { if (!initial) setForm({ rule: "", channels: [], enforcement: "checklist" }); notify.success(L.saved); await onSaved(); },
  });
  const set = <K extends keyof C.CreateWorkspacePolicyInput>(key: K, value: C.CreateWorkspacePolicyInput[K]) => { save.reset(); setForm({ ...form, [key]: value }); };
  const submit = (event: FormEvent) => { event.preventDefault(); if (form.channels.length > 0) save.mutate(); };
  return <form className="z-form z-flow" onSubmit={submit}>
    <Field label={L.rule}><textarea className="z-textarea" aria-label={L.rule} value={form.rule} onChange={(e) => set("rule", e.target.value)} minLength={5} maxLength={1000} required /></Field>
    <Field label={L.channels} help={L.channelsHelp}><ul className="z-check">{options.map((channel) => {
      const id = "workspace-policy-" + (initial?.id ?? "new") + "-" + channel;
      return <li key={channel}><input id={id} type="checkbox" checked={form.channels.includes(channel)} onChange={(e) => set("channels", e.target.checked ? [...form.channels, channel] : form.channels.filter((c) => c !== channel))} /><label htmlFor={id}><ChannelName channel={channel} /></label></li>;
    })}</ul></Field>
    <Field label={L.application} help={L.applicationHelp}><Select variant="form" label={L.application} value={form.enforcement} onValueChange={(nextValue) => set("enforcement", nextValue as C.CreateWorkspacePolicyInput["enforcement"])} options={Object.entries(L.applicationOptions).map(([value, label]) => ({ value, label }))} /></Field>
    <p className="z-small z-muted">{L.policyNote}</p>
    <div><Button tone="primary" type="submit" busy={save.isPending} disabled={!form.channels.length}>{initial ? L.saveChanges : L.save}</Button></div>
    <ErrorText error={save.error} />
  </form>;
}
